import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, TrendingUp } from 'lucide-react';
import { useAuth } from './context/AuthContext';
import GoogleSignIn from './components/GoogleSignIn';
import UserProfile from './components/UserProfile';
import MarketingContent from './components/MarketingContent';
import { financialAPI } from './api/api';

function CashflowTracker() {
  const [activeTab, setActiveTab] = useState('setup');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [job, setJob] = useState('');
  const [salary, setSalary] = useState('');
  
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  
  const [newIncome, setNewIncome] = useState({ name: '', amount: '' });
  const [newExpense, setNewExpense] = useState({ name: '', amount: '' });
  const [newAsset, setNewAsset] = useState({ name: '', cost: '', type: 'real-estate', cashflow: '' });
  const [newLiability, setNewLiability] = useState({ name: '', amount: '', payment: '' });

  // Load financial data on mount
  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const response = await financialAPI.getFinancialData();
      const data = response.data;
      
      setJob(data.job || '');
      setSalary(data.salary || '');
      
      // Convert MongoDB _id to id for frontend compatibility
      setIncomes((data.incomes || []).map(item => ({ ...item, id: item._id || item.id })));
      setExpenses((data.expenses || []).map(item => ({ ...item, id: item._id || item.id })));
      setAssets((data.assets || []).map(item => ({ ...item, id: item._id || item.id })));
      setLiabilities((data.liabilities || []).map(item => ({ ...item, id: item._id || item.id })));
    } catch (error) {
      console.error('Failed to load financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save basic info (job and salary) with debounce
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(async () => {
        try {
          setSaving(true);
          await financialAPI.updateBasicInfo(job, salary);
        } catch (error) {
          console.error('Failed to save basic info:', error);
        } finally {
          setSaving(false);
        }
      }, 1000); // Debounce 1 second
      
      return () => clearTimeout(timer);
    }
  }, [job, salary, loading]);

  // Calculate totals
  const passiveIncome = incomes.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  const totalIncome = parseFloat(salary || 0) + passiveIncome;
  
  const liabilityPayments = liabilities.reduce((sum, item) => sum + parseFloat(item.payment || 0), 0);
  const totalExpenses = expenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) + liabilityPayments;
  
  const cashFlow = totalIncome - totalExpenses;
  const payday = cashFlow;
  
  const totalAssetCost = assets.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
  const totalLiabilities = liabilities.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

  const addIncome = async () => {
    if (newIncome.name && newIncome.amount) {
      try {
        const response = await financialAPI.addIncome(newIncome.name, newIncome.amount);
        const data = response.data;
        setIncomes(data.incomes.map(item => ({ ...item, id: item._id || item.id })));
        setNewIncome({ name: '', amount: '' });
      } catch (error) {
        console.error('Failed to add income:', error);
        alert('Failed to add income. Please try again.');
      }
    }
  };

  const addExpense = async () => {
    if (newExpense.name && newExpense.amount) {
      try {
        const response = await financialAPI.addExpense(newExpense.name, newExpense.amount);
        const data = response.data;
        setExpenses(data.expenses.map(item => ({ ...item, id: item._id || item.id })));
        setNewExpense({ name: '', amount: '' });
      } catch (error) {
        console.error('Failed to add expense:', error);
        alert('Failed to add expense. Please try again.');
      }
    }
  };

  const addAsset = async () => {
    if (newAsset.name && newAsset.cost) {
      try {
        const response = await financialAPI.addAsset(
          newAsset.name, 
          newAsset.cost, 
          newAsset.type, 
          newAsset.cashflow || 0
        );
        const data = response.data;
        setAssets(data.assets.map(item => ({ ...item, id: item._id || item.id })));
        setIncomes(data.incomes.map(item => ({ ...item, id: item._id || item.id })));
        setNewAsset({ name: '', cost: '', type: 'real-estate', cashflow: '' });
      } catch (error) {
        console.error('Failed to add asset:', error);
        alert('Failed to add asset. Please try again.');
      }
    }
  };

  const addLiability = async () => {
    if (newLiability.name && newLiability.amount && newLiability.payment) {
      try {
        const response = await financialAPI.addLiability(
          newLiability.name, 
          newLiability.amount, 
          newLiability.payment
        );
        const data = response.data;
        setLiabilities(data.liabilities.map(item => ({ ...item, id: item._id || item.id })));
        setNewLiability({ name: '', amount: '', payment: '' });
      } catch (error) {
        console.error('Failed to add liability:', error);
        alert('Failed to add liability. Please try again.');
      }
    }
  };

  const deleteIncome = async (id) => {
    try {
      const response = await financialAPI.deleteIncome(id);
      const data = response.data;
      setIncomes(data.incomes.map(item => ({ ...item, id: item._id || item.id })));
    } catch (error) {
      console.error('Failed to delete income:', error);
      alert('Failed to delete income. Please try again.');
    }
  };

  const deleteExpense = async (id) => {
    try {
      const response = await financialAPI.deleteExpense(id);
      const data = response.data;
      setExpenses(data.expenses.map(item => ({ ...item, id: item._id || item.id })));
    } catch (error) {
      console.error('Failed to delete expense:', error);
      alert('Failed to delete expense. Please try again.');
    }
  };

  const deleteAsset = async (id) => {
    try {
      const response = await financialAPI.deleteAsset(id);
      const data = response.data;
      setAssets(data.assets.map(item => ({ ...item, id: item._id || item.id })));
      setIncomes(data.incomes.map(item => ({ ...item, id: item._id || item.id })));
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  };

  const deleteLiability = async (id) => {
    try {
      const response = await financialAPI.deleteLiability(id);
      const data = response.data;
      setLiabilities(data.liabilities.map(item => ({ ...item, id: item._id || item.id })));
    } catch (error) {
      console.error('Failed to delete liability:', error);
      alert('Failed to delete liability. Please try again.');
    }
  };

  const stockAssets = assets.filter(a => a.type === 'stocks');
  const realEstateAssets = assets.filter(a => a.type === 'real-estate');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white p-6 rounded-t-2xl shadow-lg">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Financial Statement Tracker</h1>
              <p className="text-purple-200 flex items-center gap-2">
                <TrendingUp size={20} />
                Increase Passive Income to Escape the Rat Race
                {saving && <span className="text-xs ml-2">(Saving...)</span>}
              </p>
            </div>
            <UserProfile />
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white shadow-md flex overflow-x-auto">
          {['setup', 'income', 'expenses', 'assets', 'liabilities', 'summary'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-semibold capitalize transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-purple-600 text-white border-b-4 border-purple-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="bg-white shadow-xl rounded-b-2xl p-6 min-h-96">
          
          {/* Setup Tab */}
          {activeTab === 'setup' && (
            <div className="max-w-2xl mx-auto space-y-6">
              <h2 className="text-2xl font-bold text-purple-800 mb-4">Setup Your Profile</h2>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Your Profession</label>
                <input
                  type="text"
                  value={job}
                  onChange={(e) => setJob(e.target.value)}
                  placeholder="e.g., Secretary, Engineer, Teacher"
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Salary (₹)</label>
                <input
                  type="number"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                  placeholder="e.g., 25000"
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
              <button
                onClick={() => setActiveTab('income')}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Continue to Income →
              </button>
            </div>
          )}

          {/* Income Tab */}
          {activeTab === 'income' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-purple-800">Income Sources</h2>
              
              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">{job || 'Job'} Salary:</span>
                  <span className="text-xl font-bold text-purple-700">₹{parseFloat(salary || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-gray-700 mb-3">Interest/Dividends</h3>
                <p className="text-sm text-gray-600 mb-3">Add passive income from stocks, dividends, or interest</p>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-gray-700 mb-3">Real Estate/Business</h3>
                <p className="text-sm text-gray-600 mb-4">Add rental income or business cash flow (added when you create assets)</p>
                <div className="space-y-2">
                  {incomes.map((income) => (
                    <div key={income.id} className="flex justify-between items-center bg-white p-3 rounded-lg border border-purple-200">
                      <span className="font-medium text-gray-700">{income.name}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-green-600 font-semibold">₹{parseFloat(income.amount).toLocaleString('en-IN')}</span>
                        <button onClick={() => deleteIncome(income.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="font-semibold text-gray-700 mb-3">Add Other Passive Income</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={newIncome.name}
                    onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                    placeholder="Source (e.g., Dividends)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-green-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                    placeholder="Monthly amount (₹)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-green-500 focus:outline-none"
                  />
                  <button
                    onClick={addIncome}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 flex items-center justify-center gap-2"
                  >
                    <PlusCircle size={20} />
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Expenses Tab */}
          {activeTab === 'expenses' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-purple-800">Monthly Expenses</h2>
              
              <div className="bg-amber-100 border-2 border-amber-400 p-4 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Liability payments (mortgage, car loan, etc.) are automatically added to your expenses.
                </p>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-gray-700 mb-3">Add Expense</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                    placeholder="Expense (e.g., Taxes, Other Expenses)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    placeholder="Monthly amount (₹)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                  />
                  <button
                    onClick={addExpense}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center justify-center gap-2"
                  >
                    <PlusCircle size={20} />
                    Add
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700">Regular Expenses</h3>
                {expenses.map((expense) => (
                  <div key={expense.id} className="flex justify-between items-center bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <span className="font-medium text-gray-700">{expense.name}:</span>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-900 font-semibold">₹{parseFloat(expense.amount).toLocaleString('en-IN')}</span>
                      <button onClick={() => deleteExpense(expense.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700">Liability Payments</h3>
                {liabilities.map((liability) => (
                  <div key={liability.id} className="flex justify-between items-center bg-orange-50 p-3 rounded-lg border border-orange-300">
                    <span className="font-medium text-gray-700">{liability.name} Payment:</span>
                    <span className="text-gray-900 font-semibold">₹{parseFloat(liability.payment).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-purple-800">Assets</h2>
              
              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h3 className="font-semibold text-gray-700 mb-3">Add Asset</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <select
                    value={newAsset.type}
                    onChange={(e) => setNewAsset({ ...newAsset, type: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                  >
                    <option value="stocks">Stocks/Funds/CDs</option>
                    <option value="real-estate">Real Estate/Business</option>
                  </select>
                  <input
                    type="text"
                    value={newAsset.name}
                    onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                    placeholder="Asset name"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={newAsset.cost}
                    onChange={(e) => setNewAsset({ ...newAsset, cost: e.target.value })}
                    placeholder="Cost/Share (₹)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                  />
                  {newAsset.type === 'real-estate' && (
                    <input
                      type="number"
                      value={newAsset.cashflow}
                      onChange={(e) => setNewAsset({ ...newAsset, cashflow: e.target.value })}
                      placeholder="Monthly cashflow (₹)"
                      className="px-4 py-2 border border-gray-300 rounded focus:border-purple-500 focus:outline-none"
                    />
                  )}
                  <button
                    onClick={addAsset}
                    className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 flex items-center justify-center gap-2"
                  >
                    <PlusCircle size={20} />
                    Add
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Note:</strong> Stocks are assets but do not generate passive income unless they pay dividends (add separately).
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2 bg-purple-600 text-white p-2 rounded">Stocks/Funds/CDs</h3>
                  <div className="space-y-2">
                    {stockAssets.map((asset) => (
                      <div key={asset.id} className="flex justify-between items-center bg-purple-50 p-3 rounded-lg border border-purple-200">
                        <span className="font-medium text-gray-700">{asset.name}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-gray-900 font-semibold">₹{parseFloat(asset.cost).toLocaleString('en-IN')}</span>
                          <button onClick={() => deleteAsset(asset.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700 mb-2 bg-purple-600 text-white p-2 rounded">Real Estate/Business</h3>
                  <div className="space-y-2">
                    {realEstateAssets.map((asset) => (
                      <div key={asset.id} className="flex justify-between items-center bg-purple-50 p-3 rounded-lg border border-purple-200">
                        <span className="font-medium text-gray-700">{asset.name}</span>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-gray-900 font-semibold">₹{parseFloat(asset.cost).toLocaleString('en-IN')}</div>
                            {asset.cashflow && <div className="text-sm text-green-600">+₹{parseFloat(asset.cashflow).toLocaleString('en-IN')}/mo</div>}
                          </div>
                          <button onClick={() => deleteAsset(asset.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Liabilities Tab */}
          {activeTab === 'liabilities' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-purple-800">Liabilities</h2>
              
              <div className="bg-amber-100 border-2 border-amber-400 p-4 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Important:</strong> Enter both the total liability amount AND the monthly payment. The monthly payment is automatically added to your expenses.
                </p>
              </div>

              <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                <h3 className="font-semibold text-gray-700 mb-3">Add Liability</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={newLiability.name}
                    onChange={(e) => setNewLiability({ ...newLiability, name: e.target.value })}
                    placeholder="Name (e.g., Home Mortgage)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={newLiability.amount}
                    onChange={(e) => setNewLiability({ ...newLiability, amount: e.target.value })}
                    placeholder="Total liability (₹)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-orange-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={newLiability.payment}
                    onChange={(e) => setNewLiability({ ...newLiability, payment: e.target.value })}
                    placeholder="Monthly payment (₹)"
                    className="px-4 py-2 border border-gray-300 rounded focus:border-orange-500 focus:outline-none"
                  />
                  <button
                    onClick={addLiability}
                    className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 flex items-center justify-center gap-2"
                  >
                    <PlusCircle size={20} />
                    Add
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {liabilities.map((liability) => (
                  <div key={liability.id} className="bg-orange-100 p-4 rounded-lg border-2 border-orange-300">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-gray-800">{liability.name}:</span>
                      <button onClick={() => deleteLiability(liability.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total: </span>
                        <span className="font-bold text-gray-900">₹{parseFloat(liability.amount).toLocaleString('en-IN')}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Monthly Payment: </span>
                        <span className="font-bold text-red-600">₹{parseFloat(liability.payment).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <div className="text-center mb-6 bg-amber-50 p-4 rounded-lg border-2 border-amber-300">
                <h3 className="text-xl font-bold text-gray-800 mb-2">INCREASE PASSIVE INCOME TO</h3>
                <h2 className="text-2xl font-bold text-gray-900">ESCAPE THE RAT RACE.</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column - Income & Expenses */}
                <div className="space-y-6">
                  {/* Income Section */}
                  <div>
                    <div className="bg-purple-700 text-white p-3 rounded-t-lg">
                      <h3 className="text-xl font-bold">INCOME</h3>
                    </div>
                    <div className="bg-purple-50 border-2 border-purple-200 rounded-b-lg p-4 space-y-3">
                      <div className="flex justify-between items-center pb-2 border-b-2 border-gray-400">
                        <span className="font-semibold"></span>
                        <span className="font-bold">Cash Flow</span>
                      </div>
                      <div className="flex justify-between pb-2 border-b border-gray-300">
                        <span className="font-medium">{job || 'Job'} Salary:</span>
                        <span className="font-semibold">₹{parseFloat(salary || 0).toLocaleString('en-IN')}</span>
                      </div>
                      <div className="pb-2 border-b-2 border-gray-400">
                        <div className="font-semibold mb-2">Interest/Dividends</div>
                      </div>
                      <div className="pb-2 border-b-2 border-gray-400">
                        <div className="font-semibold mb-2">Real Estate/Business</div>
                        {incomes.map((income) => (
                          <div key={income.id} className="flex justify-between ml-4 mb-1">
                            <span className="text-sm">{income.name}</span>
                            <span className="text-sm font-semibold">₹{parseFloat(income.amount).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Expenses Section */}
                  <div>
                    <div className="bg-purple-700 text-white p-3 rounded-t-lg">
                      <h3 className="text-xl font-bold">EXPENSES</h3>
                    </div>
                    <div className="bg-purple-50 border-2 border-purple-200 rounded-b-lg p-4 space-y-3">
                      {expenses.map((expense) => (
                        <div key={expense.id} className="flex justify-between pb-2 border-b border-gray-300">
                          <span className="font-medium">{expense.name}:</span>
                          <span className="font-semibold">₹{parseFloat(expense.amount).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                      {liabilities.map((liability) => (
                        <div key={liability.id} className="flex justify-between pb-2 border-b border-gray-300">
                          <span className="font-medium">{liability.name} Payment:</span>
                          <span className="font-semibold">₹{parseFloat(liability.payment).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column - Summary Cards */}
                <div className="space-y-6">
                  {/* Goal Card */}
                  <div className="bg-amber-100 border-2 border-purple-700 rounded-lg p-6">
                    <div className="text-center mb-4">
                      <div className="bg-purple-700 text-white p-3 rounded-lg inline-block">
                        <p className="text-sm font-semibold">TOTAL EXPENSES: ₹{totalExpenses.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                    {/* Progress Bar with Triangle */}
                    <div className="mb-4 relative">
                      {(() => {
                        const progressPercent = totalExpenses > 0 ? Math.min((passiveIncome / totalExpenses) * 100, 100) : 0;
                        let triangleColor = 'border-t-purple-700';
                        
                        if (progressPercent >= 100) {
                          triangleColor = 'border-t-green-600';
                        } else if (progressPercent >= 75) {
                          triangleColor = 'border-t-green-500';
                        } else if (progressPercent >= 50) {
                          triangleColor = 'border-t-yellow-500';
                        } else if (progressPercent >= 25) {
                          triangleColor = 'border-t-orange-500';
                        } else {
                          triangleColor = 'border-t-red-500';
                        }
                        
                        return (
                          <>
                            {/* Triangle positioned at the end of progress */}
                            <div className="relative mb-2" style={{ height: '30px' }}>
                              <div 
                                className={`absolute w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] ${triangleColor} transition-all duration-500 animate-pulse`}
                                style={{ 
                                  left: `calc(${progressPercent}% - 20px)`,
                                  top: '0'
                                }}
                              ></div>
                            </div>
                            {/* Progress Bar */}
                            <div className="w-full bg-gray-300 rounded-full h-8 border-2 border-purple-700 overflow-hidden relative">
                              <div 
                                className="bg-purple-700 h-full rounded-full transition-all duration-500 flex items-center justify-center"
                                style={{ 
                                  width: `${progressPercent}%` 
                                }}
                              >
                                {passiveIncome > 0 && (
                                  <span className="text-white text-xs font-bold">
                                    {Math.round(progressPercent)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-800 mb-1">PASSIVE INCOME:</p>
                      <p className="text-2xl font-bold text-gray-900">₹{passiveIncome.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Cash Summary */}
                  <div className="bg-amber-50 border-2 border-gray-400 rounded-lg p-6 space-y-3">
                    <div className="flex justify-between items-center text-2xl">
                      <span className="font-bold">CASH:</span>
                      <span className="font-bold">₹{Math.abs(cashFlow).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="border-t-2 border-gray-400 pt-2 space-y-2">
                      <div className="flex justify-between">
                        <span className="font-semibold">TOTAL INCOME:</span>
                        <span className="font-semibold">₹{totalIncome.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold">TOTAL EXPENSES:</span>
                        <span className="font-semibold">₹-{totalExpenses.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="border-t-2 border-gray-400 pt-2"></div>
                      <div className="flex justify-between text-lg">
                        <span className="font-bold">PAYDAY</span>
                        <span className="font-bold">₹{payday.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Assets & Liabilities */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="bg-purple-700 text-white p-3 rounded-t-lg">
                        <h4 className="font-bold">ASSETS</h4>
                      </div>
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-b-lg p-4">
                        <div className="font-semibold mb-2">Total: ₹{totalAssetCost.toLocaleString('en-IN')}</div>
                        <div className="text-sm space-y-1">
                          {assets.map((asset) => (
                            <div key={asset.id} className="flex justify-between">
                              <span>{asset.name}</span>
                              <span>₹{parseFloat(asset.cost).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="bg-purple-700 text-white p-3 rounded-t-lg">
                        <h4 className="font-bold">LIABILITIES</h4>
                      </div>
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-b-lg p-4">
                        <div className="font-semibold mb-2">Total: ₹{totalLiabilities.toLocaleString('en-IN')}</div>
                        <div className="text-sm space-y-1">
                          {liabilities.map((liability) => (
                            <div key={liability.id} className="flex justify-between">
                              <span>{liability.name}</span>
                              <span>₹{parseFloat(liability.amount).toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center p-4">
        <div className="max-w-6xl mx-auto w-full grid md:grid-cols-2 gap-8 items-stretch">
          <MarketingContent />
          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-purple-100 flex flex-col justify-between">
            <div>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-purple-800 mb-2">Financial Statement Tracker</h1>
                <p className="text-gray-600">
                  Sign in to build your cashflow statement and monitor passive income milestones.
                </p>
              </div>
              <div id="signin" className="w-full">
                <GoogleSignIn />
              </div>
            </div>
            <div className="text-sm text-gray-500 text-center mt-6">
              <p>By signing in, you agree to our terms of service and privacy policy.</p>
              <p className="mt-4 text-gray-600">
                Financial Statement Tracker helps entrepreneurs, salary earners, and finance coaches visualise the exact
                path to financial independence.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <CashflowTracker />;
}
